/**
 * 公共头部，基于 ant design 的 PageHeader 封装
 * @author jasonelchen
 */
import React, { FunctionComponent } from 'react'
import classNames from 'classnames'
import { useHistory } from 'react-router-dom'
import { PageHeader as AntPageHeader, Tooltip } from 'antd'
import { PushpinOutlined, PushpinFilled } from '@ant-design/icons'
import { PageHeaderProps as AntPageHeaderProps } from 'antd/es/page-header'
import './style.less'
import { useTitle } from '@hooks'
import { isString } from '@library'
import { globalStore } from '@store'
import NotificationBell from '../NotificationBell'

interface PageHeaderProps extends AntPageHeaderProps {
  hasBack?: boolean
  documentTitle?: string // 当 title 不为 string 是，替代使用的此参
}

export const PageHeader: FunctionComponent<React.PropsWithChildren<PageHeaderProps>> = props => {
  const {
    documentTitle,
    hasBack = false,
    onBack,
    className,
    ghost = false,
    title,
    extra,
    ...restProps
  } = props
  const { goBack } = useHistory()

  // 标题栏是否固定吸顶（全局偏好：所有页面共享，并持久化到本地）
  const pinned = globalStore(s => s.headerPinned)
  const setPinned = globalStore(s => s.setHeaderPinned)

  useTitle(isString(title) ? title : documentTitle)

  // 标题右侧的「固定/取消固定」图钉
  const titleNode = (
    <span className="component-page-header__title">
      {title}
      <Tooltip title={pinned ? '点击取消固定顶部' : '点击固定顶部'}>
        {pinned ? (
          <PushpinFilled
            className="component-page-header__pin is-pinned"
            onClick={() => setPinned(false)}
          />
        ) : (
          <PushpinOutlined
            className="component-page-header__pin"
            onClick={() => setPinned(true)}
          />
        )}
      </Tooltip>
    </span>
  )

  // 铃铛并入 extra 最右端（页面原有操作按钮之后）
  const extraNode = (
    <>
      {extra}
      <NotificationBell />
    </>
  )

  return (
    <AntPageHeader
      ghost={ghost}
      className={classNames('component-page-header', { 'is-pinned': pinned }, className)}
      onBack={hasBack ? (onBack || goBack) : onBack}
      title={titleNode}
      extra={extraNode}
      {...restProps}
    />
  )
}
